import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import logo from '../../photos/logo.png';

function Buh() {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const logoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const els = [logoRef.current, titleRef.current, subtitleRef.current];
    els.forEach((el, i) => {
      if (el) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        setTimeout(() => {
          el.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, 300 + i * 200);
      }
    });
  }, []);

  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 100);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // Particles
    const count = 600;
    const positions = new Float32Array(count * 3);
    const speeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
      speeds[i] = Math.random() * 0.004 + 0.001;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      size: 0.035,
      color: 0x16b724,
      transparent: true,
      opacity: 0.5,
      sizeAttenuation: true,
      depthWrite: false,
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // Glow ring
    const ringGeometry = new THREE.TorusGeometry(3.2, 0.015, 16, 100);
    const ringMaterial = new THREE.MeshBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.2 });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.z = -2;
    scene.add(ring);

    // Mouse interaction
    const mouse = { x: 0, y: 0 };
    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };
    container.addEventListener('mousemove', handleMouseMove);

    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      const posArray = geometry.attributes.position.array;
      for (let i = 0; i < count; i++) {
        posArray[i * 3 + 1] += speeds[i];
        if (posArray[i * 3 + 1] > 10) posArray[i * 3 + 1] = -10;
      }
      geometry.attributes.position.needsUpdate = true;

      const time = Date.now() * 0.001;
      points.rotation.y = time * 0.03 + mouse.x * 0.3;
      points.rotation.x = mouse.y * 0.2;
      ring.rotation.z = time * 0.1;
      ring.rotation.x = Math.sin(time * 0.2) * 0.3 + mouse.y * 0.2;

      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      container.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      ringGeometry.dispose();
      ringMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <section style={{
      minHeight: 'clamp(500px, 80vh, 100vh)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #0a0a0f 0%, #0d1a0e 50%, #0a0a0f 100%)',
      padding: '100px 24px 60px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Three.js Particles */}
      <div ref={canvasRef} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }} />

      {/* Gradient overlays */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 200,
        background: 'linear-gradient(180deg, #0a0a0f, transparent)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 200,
        background: 'linear-gradient(0deg, #0a0a0f, transparent)',
        zIndex: 1,
      }} />

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 800 }}>
        <img ref={logoRef} src={logo} alt="The Hot Logistics" style={{
          width: '100%',
          maxWidth: 300,
          marginBottom: 32,
          filter: 'drop-shadow(0 0 40px rgba(22,183,36,0.2))',
        }} />
        <h1 ref={titleRef} style={{
          fontSize: 'clamp(24px, 4vw, 46px)',
          fontWeight: 700,
          color: '#ffffff',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          lineHeight: 1.2,
          marginBottom: 20,
        }}>
          Фінансові рішення для{' '}
          <span style={{
            background: 'linear-gradient(135deg, #16b724, #4dc247)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>ефективної</span>{' '}
          бухгалтерії
        </h1>
        <p ref={subtitleRef} style={{
          fontSize: 'clamp(15px, 2vw, 18px)',
          color: 'rgba(255,255,255,0.5)',
          maxWidth: 500,
          margin: '0 auto 40px',
          lineHeight: 1.6,
        }}>
          Професійний бухгалтерський супровід для вашого бізнесу
        </p>
        <a href="tel:+380960968402" style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '16px 40px',
          background: 'linear-gradient(135deg, #16b724, #0e8a1a)',
          color: '#fff',
          fontSize: 16,
          fontWeight: 600,
          textDecoration: 'none',
          borderRadius: 50,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 30px rgba(22, 183, 36, 0.4)',
        }}
        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 40px rgba(22, 183, 36, 0.5)'; }}
        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 30px rgba(22, 183, 36, 0.4)'; }}
        >
          Зателефонувати
        </a>
      </div>
    </section>
  );
}

export default Buh;
